import { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useParams,
} from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AuthLayout } from "@/components/layout/AuthLayout";

// Auth pages
import { LoginPage } from "@/pages/auth/LoginPage";
import { SelectUserPage } from "@/pages/auth/SelectUserPage";
import { ForgotPasswordPage } from "@/pages/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/auth/ResetPasswordPage";

// Standalone pages (hors shell — pas de sidebar)
import { BiensPrintPage } from "@/pages/biens/BiensPrintPage";
import { MailingPage } from "@/pages/mailing/MailingPage";

// V2 — Skyline OS (refonte expérimentale, branche feat/v2-skyline-os)
import { RoleProvider } from "@/contexts/RoleContext";
import { V2Routes } from "@/v2/V2Routes";

// Module temporaire /publi-temp — admin externe qui publie des biens
// sur le site web sans accès au reste de l'app. Gate login+mdp en dur.
import { TempPublishLoginPage } from "@/pages/publi-temp/TempPublishLoginPage";
import { TempPublishListPage } from "@/pages/publi-temp/TempPublishListPage";
import { TempPublishDetailPage } from "@/pages/publi-temp/TempPublishDetailPage";
import { TempPublishGuard } from "@/pages/publi-temp/TempPublishGuard";

/**
 * Route toujours un lien de récupération de mot de passe vers /reset-password,
 * même si le mail ne précise pas de redirectTo (cas d'un envoi via le dashboard
 * Supabase ou l'API recover sans redirectTo) — sinon la session recovery
 * authentifie l'utilisateur et la redirection home l'emmène dans l'app au lieu
 * de l'écran "nouveau mot de passe".
 */
function RecoveryRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        navigate("/reset-password", { replace: true });
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);
  return null;
}

/**
 * Redirige une route V1 paramétrée vers son équivalent V2 en préservant les
 * params (id / section). Utilisé pour la sortie de V1 : toutes les routes V1
 * deviennent des redirections vers le shell V2.
 */
function RedirectWithParams({
  to,
}: {
  to: (p: Record<string, string | undefined>) => string;
}) {
  const p = useParams();
  return <Navigate to={to(p)} replace />;
}

export default function App() {
  return (
    <AuthProvider>
    <BrowserRouter>
      <RecoveryRedirect />
      <Routes>
        {/* Public auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/select-user" element={<SelectUserPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Page de garde du module temporaire /publi-temp. Public (pas dans
              ProtectedRoute) parce qu'il doit s'auto-authentifier via le mdp.
              Une fois validé, navigate('/publi-temp') passe par TempPublishGuard
              qui requiert la dev session admin + sessionStorage gate. */}
          <Route path="/publi-temp/login" element={<TempPublishLoginPage />} />
        </Route>

        {/* Module /publi-temp — déclaré AVANT ProtectedRoute / AppLayout pour
            avoir son propre layout minimaliste (pas de sidebar standard). */}
        <Route element={<TempPublishGuard />}>
          <Route path="/publi-temp" element={<TempPublishListPage />} />
          <Route path="/publi-temp/:id" element={<TempPublishDetailPage />} />
        </Route>

        {/* Protected app routes */}
        <Route element={<ProtectedRoute />}>

        {/* V2 — Skyline OS (déclaré AVANT AppLayout pour avoir son propre layout) */}
        <Route
          path="/v2/*"
          element={
            <RoleProvider>
              <V2Routes />
            </RoleProvider>
          }
        />

        {/* Sortie de V1 — toutes les anciennes routes V1 redirigent vers le
            shell V2. Les params (id / section) sont préservés. Le shell V1
            (AppLayout / Sidebar / Header) a été supprimé. */}
        <Route path="/" element={<Navigate to="/v2" replace />} />
        <Route path="/dashboard" element={<Navigate to="/v2/admin/dashboard" replace />} />

        {/* Biens */}
        <Route path="/biens" element={<Navigate to="/v2/biens" replace />} />
        {/* Sous-menus CEO (préparation / suivi-els / suivi-srs / mailing) :
            le V2 dashboard a remplacé le parcours CEO. */}
        <Route path="/biens/preparation" element={<Navigate to="/v2/dashboard" replace />} />
        <Route path="/biens/suivi-els" element={<Navigate to="/v2/dashboard" replace />} />
        <Route path="/biens/suivi-srs" element={<Navigate to="/v2/dashboard" replace />} />
        <Route path="/biens/mailing" element={<Navigate to="/v2/dashboard" replace />} />
        <Route
          path="/biens/preparation/:id"
          element={<RedirectWithParams to={(p) => `/v2/biens/${p.id}`} />}
        />
        <Route
          path="/biens/suivi-els/:id"
          element={<RedirectWithParams to={(p) => `/v2/biens/${p.id}`} />}
        />
        <Route
          path="/biens/suivi-srs/:id"
          element={<RedirectWithParams to={(p) => `/v2/biens/${p.id}`} />}
        />
        <Route
          path="/biens/mailing/:id"
          element={<RedirectWithParams to={(p) => `/v2/biens/${p.id}`} />}
        />
        <Route
          path="/biens/:id"
          element={<RedirectWithParams to={(p) => `/v2/biens/${p.id}`} />}
        />
        <Route
          path="/biens/:id/:section"
          element={<RedirectWithParams to={(p) => `/v2/biens/${p.id}/${p.section}`} />}
        />

        {/* Interventions */}
        <Route path="/interventions" element={<Navigate to="/v2/interventions" replace />} />

        {/* Publication — Planning photos */}
        <Route path="/planning-photos" element={<Navigate to="/v2/publication" replace />} />
        <Route
          path="/planning-photos/:id"
          element={<RedirectWithParams to={(p) => `/v2/biens/${p.id}`} />}
        />

        {/* Publication — Suivi publication */}
        <Route path="/suivi-publication" element={<Navigate to="/v2/publication" replace />} />
        <Route
          path="/suivi-publication/:id"
          element={<RedirectWithParams to={(p) => `/v2/biens/${p.id}`} />}
        />

        {/* Prestataires — la gestion d'équipe V2 couvre les prestataires. */}
        <Route path="/prestataires" element={<Navigate to="/v2/admin/equipe" replace />} />
        <Route path="/prestataires/:id" element={<Navigate to="/v2/admin/equipe" replace />} />
        <Route
          path="/prestataires/:id/:section"
          element={<Navigate to="/v2/admin/equipe" replace />}
        />

        {/* Apparts for Rent (admin) */}
        <Route path="/apparts-for-rent" element={<Navigate to="/v2/apparts-for-rent" replace />} />
        <Route
          path="/apparts-for-rent/:id"
          element={<RedirectWithParams to={(p) => `/v2/biens/${p.id}`} />}
        />

        {/* Suivi ELS (collaborateur) */}
        <Route path="/suivi-els" element={<Navigate to="/v2/pipeline" replace />} />
        <Route
          path="/suivi-els/:id"
          element={<RedirectWithParams to={(p) => `/v2/biens/${p.id}`} />}
        />

        {/* Collaborateur Apartments for Rent — alias de la page unifiée */}
        <Route path="/collaborateur/apparts" element={<Navigate to="/v2/apparts-for-rent" replace />} />
        <Route
          path="/collaborateur/apparts/:id"
          element={<RedirectWithParams to={(p) => `/v2/biens/${p.id}`} />}
        />

        {/* État des lieux (prestataire) */}
        <Route path="/etat-des-lieux" element={<Navigate to="/v2/missions" replace />} />
        <Route path="/etat-des-lieux/:id" element={<Navigate to="/v2/missions" replace />} />

        {/* Audit PEB (prestataire) */}
        <Route path="/audit-peb" element={<Navigate to="/v2/missions" replace />} />
        <Route path="/audit-peb/:id" element={<Navigate to="/v2/missions" replace />} />

        {/* Paramètres */}
        <Route path="/parametres/metiers" element={<Navigate to="/v2/admin/metiers" replace />} />
        <Route path="/parametres/quartiers" element={<Navigate to="/v2/admin/quartiers" replace />} />
        <Route
          path="/parametres/quartiers/:id"
          element={<RedirectWithParams to={(p) => `/v2/admin/quartiers/${p.id}`} />}
        />
        <Route path="/parametres/equipe" element={<Navigate to="/v2/admin/equipe" replace />} />
        <Route path="/parametres/traductions" element={<Navigate to="/v2/admin/traductions" replace />} />
        <Route path="/parametres/droits-champs" element={<Navigate to="/v2/admin/droits-champs" replace />} />
        <Route path="/parametres/import-xlsx" element={<Navigate to="/v2/admin/import" replace />} />
        <Route
          path="/parametres/administration"
          element={<Navigate to="/v2/admin/administration" replace />}
        />

        {/* Mailing — standalone, no sidebar */}
        <Route path="/mailing" element={<MailingPage />} />

        {/* Biens print — standalone, no sidebar (for CEO meeting prep PDF) */}
        {/* Path "/biens-print" avec tiret pour eviter le conflit avec /biens/:id */}
        <Route path="/biens-print" element={<BiensPrintPage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
    </AuthProvider>
  );
}
